import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, filter, finalize, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BoardContactService } from '../../../../services/board-contact.service';
import { BoardService } from '../../../../services/board.service';
import { DialogService } from '../../../../services/dialog.service';
import { NotificationService } from '../../../../services/notifications.service';
import { StackedSidebarService } from '../../../../services/stacked-sidebar.service';
import { SidebarType } from '../../../layout/stacked-sidebar/stacked-sidebar.interface';
import { FUNNEL_TYPE } from '../../../../enum/constants';
import {
  BoardContact,
  BoardContactCard,
  BoardField,
  BoardFunnel,
  UpdateContactPayload,
} from '../../board-v3.types';
import { TIMELINE_FILTERS, TimelineComponent } from '../../../timeline/timeline.component';
import { TIMELINE_ENTITY_TYPE } from '../../../../interfaces/timeline.interface';
import {
  customFieldEmailValidator,
  customFieldPhoneValidator,
  normalizePhonePayloadValue,
  sanitizePhoneInputValue,
} from '../../custom-field.validators';
import { AddressInput } from '../../../../interfaces/card';
import { formatAddress, getAddressMapUrl, toAddressInput } from '../../address.utils';
import {
  formatContactPhone,
  getContactEmails,
  getContactErrorKey,
  getContactName,
  getContactPhones,
} from '../contact.utils';

type EditableContactField = 'first_name' | 'last_name' | 'middle_name' | 'phones' | 'emails' | 'addresses';

export interface ContactDetailResult {
  /** Latest state of the contact when it was changed */
  contact?: BoardContact;
  deleted?: boolean;
}

@Component({
  selector: 'app-board-v3-contact-detail',
  templateUrl: './contact-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full w-full min-h-0' },
})
export class BoardV3ContactDetailComponent implements OnChanges {
  @Input() contactId: string | null = null;
  @Input() canOpenCards = true;
  @Output() close = new EventEmitter<ContactDetailResult | void>();
  @ViewChildren('editableControl') private editableControls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild(TimelineComponent) private timeline?: TimelineComponent;

  private readonly contactService = inject(BoardContactService);
  private readonly boardService = inject(BoardService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected contact: BoardContact | null = null;
  protected loading = false;
  protected busy = false;
  protected saving = false;
  protected showCloseConfirmation = false;
  protected activeField: EditableContactField | null = null;
  protected readonly nameFields: { key: 'last_name' | 'first_name' | 'middle_name'; label: string }[] = [
    { key: 'last_name', label: 'last_name' },
    { key: 'first_name', label: 'first_name' },
    { key: 'middle_name', label: 'middle_name' },
  ];
  protected readonly form = new FormGroup({
    first_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    last_name: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    middle_name: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
  });
  protected phonesArray = new FormArray<FormControl<string>>([]);
  protected emailsArray = new FormArray<FormControl<string>>([]);
  protected readonly addressesControl = new FormControl<AddressInput[]>([], { nonNullable: true });
  protected readonly getAddressMapUrl = getAddressMapUrl;
  private changed = false;
  private funnels = new Map<string, BoardFunnel>();

  protected readonly TIMELINE_ENTITY_TYPE = TIMELINE_ENTITY_TYPE;
  protected readonly TIMELINE_FILTERS = TIMELINE_FILTERS;
  protected fieldDefinitions = new Map<string, BoardField>();
  private loadedFieldFunnels = new Set<string>();

  protected readonly getContactName = getContactName;
  protected readonly formatContactPhone = formatContactPhone;

  get phones(): string[] {
    return this.contact ? getContactPhones(this.contact) : [];
  }

  get emails(): string[] {
    return this.contact ? getContactEmails(this.contact) : [];
  }

  get hasChanges(): boolean {
    return this.form.dirty || this.phonesArray.dirty || this.emailsArray.dirty || this.addressesControl.dirty;
  }

  /** Name from the form, so the header follows the edits */
  get displayName(): string {
    return getContactName(this.form.getRawValue()) || getContactName(this.contact);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactId'] && this.contactId) this.load(this.contactId);
  }

  protected getFunnelName(card: BoardContactCard): string {
    return this.funnels.get(card.funnel_id)?.name ?? '';
  }

  protected getCardTypeKey(card: BoardContactCard): string {
    return this.funnels.get(card.funnel_id)?.type === FUNNEL_TYPE.LEAD
      ? 'board_v3_contact_card_lead'
      : 'board_v3_contact_card_deal';
  }

  protected formatAddress(address: AddressInput): string {
    return formatAddress(address, this.translate('address_apartment_short'));
  }

  protected phoneHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, '')}`;
  }

  protected openCard(card: BoardContactCard): void {
    if (!this.canOpenCards) return;

    this.stackedSidebarService
      .openSidebar(
        { type: SidebarType.CardV3, width: '90vw' },
        { card_id: card.id, type: this.funnels.get(card.funnel_id)?.type ?? FUNNEL_TYPE.DEAL },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.contactId && this.load(this.contactId, false));
  }

  protected reload(): void {
    if (this.contactId) this.load(this.contactId, false);
  }

  // #region inline editing

  protected editField(field: EditableContactField): void {
    this.activeField = field;
    setTimeout(() => {
      const root = this.editableControls?.first?.nativeElement;
      const control = root instanceof HTMLInputElement ? root : root?.querySelector('input');
      control?.focus();
      control?.select();
    });
  }

  protected isFieldEditing(field: EditableContactField): boolean {
    return this.activeField === field;
  }

  @HostListener('document:click', ['$event'])
  protected closeActiveFieldOnOutsideClick(event: MouseEvent): void {
    if (!this.activeField) return;

    const target = event.target as Node | null;
    if (!target) return;

    const targetElement = target instanceof Element ? target : target.parentElement;
    if (targetElement?.closest('[data-contact-edit-trigger="true"], .pac-container')) return;
    if (this.editableControls?.toArray().some((control) => control.nativeElement.contains(target))) return;

    this.activeField = null;
  }

  protected onTextFieldBlur(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement | null;
    const nextTarget = event.relatedTarget as Node | null;
    if (currentTarget && nextTarget && currentTarget.contains(nextTarget)) return;

    this.activeField = null;
  }

  protected hasFieldError(field: 'first_name' | 'last_name' | 'middle_name'): boolean {
    const control = this.form.controls[field];
    return control.invalid && control.touched;
  }

  protected hasRowValues(field: 'phones' | 'emails'): boolean {
    return this.getFieldArray(field).controls.some((control) => control.value.trim());
  }

  protected getFieldArray(field: 'phones' | 'emails'): FormArray<FormControl<string>> {
    return field === 'phones' ? this.phonesArray : this.emailsArray;
  }

  protected addRow(field: 'phones' | 'emails'): void {
    const array = this.getFieldArray(field);
    array.push(this.createRowControl(field, ''));
    array.markAsDirty();
  }

  protected removeRow(field: 'phones' | 'emails', index: number): void {
    const array = this.getFieldArray(field);
    if (array.length <= 1) {
      array.at(0).setValue('');
    } else {
      array.removeAt(index);
    }
    array.markAsDirty();
  }

  protected onPhoneInput(control: FormControl<string>, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizePhoneInputValue(input.value);
    if (value === input.value) return;

    input.value = value;
    control.setValue(value);
  }

  protected cancelChanges(): void {
    if (this.saving || !this.contact) return;

    this.showCloseConfirmation = false;
    this.patchForm(this.contact);
  }

  protected save(closeAfterSave = false): void {
    const contact = this.contact;
    if (this.saving || !contact) return;

    this.form.markAllAsTouched();
    this.phonesArray.markAllAsTouched();
    this.emailsArray.markAllAsTouched();
    // The editor's validator is detached once the field leaves edit mode, but the status is not recalculated
    this.addressesControl.updateValueAndValidity({ emitEvent: false });
    if (this.form.invalid || this.phonesArray.invalid || this.emailsArray.invalid || this.addressesControl.invalid) {
      this.showCloseConfirmation = false;
      return;
    }

    const payload = this.buildPayload();
    if (!Object.keys(payload).length) {
      this.patchForm(contact);
      if (closeAfterSave) this.emitClose();
      return;
    }

    this.saving = true;
    this.activeField = null;
    this.contactService
      .updateContact(contact.id, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (saved) => {
          this.contact = saved;
          this.changed = true;
          this.patchForm(saved);
          this.notificationService.showSuccess('', this.translate('board_v3_contact_saved'));
          if (closeAfterSave) this.emitClose();
        },
        error: (error) => {
          this.showCloseConfirmation = false;
          this.notificationService.showError('', this.translate(getContactErrorKey(error)));
        },
      });
  }

  /** Called by the header button and by the sidebar backdrop / escape */
  requestClose(): void {
    if (this.saving || this.showCloseConfirmation) return;

    this.activeField = null;
    if (!this.hasChanges) {
      this.emitClose();
      return;
    }

    this.showCloseConfirmation = true;
  }

  protected cancelClose(): void {
    this.showCloseConfirmation = false;
  }

  protected discardChangesAndClose(): void {
    if (this.saving) return;

    this.emitClose();
  }

  private emitClose(): void {
    this.showCloseConfirmation = false;
    this.close.emit(this.changed && this.contact ? { contact: this.contact } : undefined);
  }

  private patchForm(contact: BoardContact): void {
    this.activeField = null;
    this.form.reset({
      first_name: contact.first_name ?? '',
      last_name: contact.last_name ?? '',
      middle_name: contact.middle_name ?? '',
    });
    this.phonesArray = this.buildRows('phones', getContactPhones(contact));
    this.emailsArray = this.buildRows('emails', getContactEmails(contact));
    this.addressesControl.reset((contact.addresses ?? []).map(toAddressInput));
  }

  private buildRows(field: 'phones' | 'emails', values: string[]): FormArray<FormControl<string>> {
    return new FormArray((values.length ? values : ['']).map((value) => this.createRowControl(field, value)));
  }

  private createRowControl(field: 'phones' | 'emails', value: string): FormControl<string> {
    const validator = field === 'phones' ? customFieldPhoneValidator : customFieldEmailValidator;
    return new FormControl(value, { nonNullable: true, validators: [validator] });
  }

  /** Only the changed fields; `phones` / `emails` / `addresses` replace the whole list */
  private buildPayload(): UpdateContactPayload {
    const raw = this.form.getRawValue();
    const controls = this.form.controls;
    const payload: UpdateContactPayload = {};

    if (controls.first_name.dirty) payload.first_name = raw.first_name.trim();
    if (controls.last_name.dirty) payload.last_name = raw.last_name.trim() || null;
    if (controls.middle_name.dirty) payload.middle_name = raw.middle_name.trim() || null;
    if (this.phonesArray.dirty) {
      payload.phones = [
        ...new Set(
          this.phonesArray.controls.map((control) => normalizePhonePayloadValue(control.value)).filter(Boolean),
        ),
      ];
    }
    if (this.emailsArray.dirty) {
      payload.emails = [
        ...new Set(this.emailsArray.controls.map((control) => control.value.trim().toLowerCase()).filter(Boolean)),
      ];
    }
    if (this.addressesControl.dirty) payload.addresses = this.addressesControl.value;

    return payload;
  }

  // #endregion

  protected remove(): void {
    const contact = this.contact;
    if (!contact || this.busy) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_contact_delete_confirm', { name: getContactName(contact) }),
        isTranslate: true,
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          this.busy = true;
          return this.contactService.deleteContact(contact.id).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.notificationService.showSuccess('', this.translate('board_v3_contact_deleted'));
          this.close.emit({ deleted: true });
        },
        error: (error) => this.notificationService.showError('', this.translate(getContactErrorKey(error))),
      });
  }

  protected unlinkCard(card: BoardContactCard): void {
    const contact = this.contact;
    if (!contact || this.busy) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_contact_unlink_card_confirm', { name: card.name }),
        isTranslate: true,
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          this.busy = true;
          return this.contactService.unlinkCard(contact.id, card.id).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => {
          this.contact = updated;
          this.changed = true;
          this.loadTimeline();
        },
        error: (error) => this.notificationService.showError('', this.translate(getContactErrorKey(error))),
      });
  }

  /** After a change of the contact: the fields of its cards and the timeline */
  protected loadTimeline(): void {
    const contact = this.contact;
    if (!contact) return;

    this.loadFieldDefinitions(contact.cards ?? [])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.timeline?.reload();
  }

  private load(contactId: string, showLoader = true): void {
    this.loading = showLoader;
    forkJoin({
      contact: this.contactService.getContact(contactId),
      leads: this.funnels.size
        ? of([])
        : this.boardService.getFunnels({ type: FUNNEL_TYPE.LEAD }).pipe(catchError(() => of([]))),
      deals: this.funnels.size
        ? of([])
        : this.boardService.getFunnels({ type: FUNNEL_TYPE.DEAL }).pipe(catchError(() => of([]))),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: ({ contact, leads, deals }) => {
          [...leads, ...deals].forEach((funnel) => this.funnels.set(funnel.id, funnel));
          this.contact = contact;
          if (!this.hasChanges) this.patchForm(contact);
          this.loadTimeline();
        },
        error: (error) => {
          this.notificationService.showError('', this.translate(getContactErrorKey(error)));
          if (!this.contact) this.close.emit();
        },
      });
  }

  /** Custom field names for the history events, loaded once per funnel */
  private loadFieldDefinitions(cards: BoardContactCard[]): Observable<void> {
    const funnelIds = [...new Set(cards.map((card) => card.funnel_id))].filter(
      (funnelId) => !this.loadedFieldFunnels.has(funnelId),
    );
    if (!funnelIds.length) return of(undefined);

    return forkJoin(
      funnelIds.map((funnelId) =>
        this.boardService.getBoardFields(funnelId, 'FUNNEL').pipe(catchError(() => of([] as BoardField[]))),
      ),
    ).pipe(
      map((fieldsByFunnel) => {
        funnelIds.forEach((funnelId) => this.loadedFieldFunnels.add(funnelId));
        const next = new Map(this.fieldDefinitions);
        fieldsByFunnel.flat().forEach((field) => next.set(field.identifier, field));
        this.fieldDefinitions = next;
      }),
    );
  }

  private translate(key: string): string {
    return String(this.translateService.instant(key));
  }
}
